/**
 * QuestionNavButton.tsx
 * Sophisticated question nav button with status indicators.
 */

import { cn } from '@vsat/shared/lib/utils';
import { Flag } from 'lucide-react';

interface Props {
  number: number;
  status: 'answered' | 'flagged' | 'unanswered';
  isFlagged?: boolean;
  isActive: boolean;
  onClick: () => void;
  className?: string;
}

export default function QuestionNavButton({
  number,
  status,
  isFlagged,
  isActive,
  onClick,
  className,
}: Props) {
  const getStatusStyle = () => {
    if (isActive)
      return 'bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg shadow-primary/25 scale-105';

    switch (status) {
      case 'answered':
        return 'bg-gradient-to-br from-success/20 to-success/30 dark:from-success/20 dark:to-success/30 text-success dark:text-success border-success/50 dark:border-success/50 hover:from-success/30 hover:to-success/40 dark:hover:from-success/30 dark:hover:to-success/40';
      case 'flagged':
        return 'bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/30 dark:to-amber-800/30 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-600 hover:from-amber-200 hover:to-amber-300 dark:hover:from-amber-800/40 dark:hover:to-amber-700/40';
      default:
        return 'bg-card text-muted-foreground border-border hover:border-primary/30 hover:bg-primary/10 dark:hover:bg-primary/20/10';
    }
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-7 h-7 rounded-md border-2 text-xs font-bold transition-all duration-200',
        'hover:scale-105 active:scale-95 focus:ring-2 focus:ring-primary/400 focus:ring-offset-1',
        getStatusStyle(),
        className
      )}
      aria-label={`Câu ${number} - ${
        status === 'answered'
          ? isFlagged
            ? 'đã trả lời, đã đánh dấu'
            : 'đã trả lời'
          : status === 'flagged'
            ? 'đã đánh dấu'
            : 'chưa trả lời'
      }`}
    >
      {/* Flag overlay for marked questions */}
      {(isFlagged || status === 'flagged') && (
        <span
          className={cn(
            'absolute -top-1.5 -left-1.5 rounded-full p-[2px]',
            // Subtle halo so it stands out on all backgrounds
            'bg-white/90 dark:bg-gray-900/70 shadow-sm'
          )}
          aria-hidden='true'
        >
          <Flag className='w-3 h-3 text-amber-600 dark:text-amber-400' />
        </span>
      )}
      {number}
    </button>
  );
}
